import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Modal, Spinner, Button } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import BootstrapCard from 'react-bootstrap/Card';
import Card from '../../../components/Card';
import { apiHandler } from '../../../api/apihandler';
import { TEACHERS_CLASS_STUDENTS_API, CHILD_REGISTRATION_VIEW } from '../../../api/constants';

// Teacher-facing "Emergency Contact" left-menu page: every student across
// every class this teacher teaches, click a student to see their contact
// numbers. Sourced from the student's registration_packages record (the
// emergency_contact_* fields, plus mother_*/father_* numbers) - the same
// source and field names the registration/attendance "View Registration"
// modal already reads (see attendance.js's viewRegistration) - not the
// separate, parent-managed EmergencyContact records.
const TeacherEmergencyContacts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailStudent, setDetailStudent] = useState(null);
  const [registrationPackage, setRegistrationPackage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiHandler(TEACHERS_CLASS_STUDENTS_API, 'POST', {});
        if (cancelled) return;
        // apiHandler() never throws - a failed request resolves with the raw
        // axios error instead, so without this check a 404/network failure
        // silently looked identical to "no students".
        if (res?.isAxiosError || (res?.response && res?.data === undefined)) {
          setError('Failed to load students.');
          return;
        }
        const list = res?.data?.data?.students;
        setStudents(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) setError('Failed to load students.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredStudents = students.filter((s) =>
    !search.trim() || s.name.toLowerCase().includes(search.trim().toLowerCase())
  );

  const handleViewDetails = async (student) => {
    setDetailStudent(student);
    setShowModal(true);
    setDetailLoading(true);
    setDetailError(null);
    setRegistrationPackage(null);
    try {
      const res = await apiHandler(CHILD_REGISTRATION_VIEW, 'POST', { student_id: student.id });
      // registration-package/show wraps its payload one extra level (see
      // attendance.js's viewRegistration for the same defensive unwrap).
      const apiData = res?.data?.data?.data || res?.data?.data || {};
      setRegistrationPackage(apiData.registration_package || null);
    } catch (err) {
      setDetailError('Failed to load contact details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setDetailStudent(null);
    setRegistrationPackage(null);
    setDetailError(null);
  };

  // Deep-link from an "Emergency contact updated" notification
  // (?studentId=<id> - see notification.js's type-18 handler) - once the
  // roster has loaded, find that student and open their detail modal
  // automatically instead of just landing on the plain list. The param is
  // stripped right after so refreshing the page doesn't keep re-opening it.
  useEffect(() => {
    const targetId = searchParams.get('studentId');
    if (!targetId || students.length === 0) return;
    const match = students.find((s) => String(s.id) === String(targetId));
    if (match) {
      handleViewDetails(match);
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('studentId');
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students]);

  const reg = registrationPackage || {};

  // Joins whichever of street/city/postal are actually present with ", " -
  // never a bare ", ," when some parts are missing.
  const formatAddress = (...parts) => parts.filter((p) => p && String(p).trim()).join(', ');

  // { label, name, phones: [{ label, value }], email, addresses: [{ label, value }] }
  // - one block per contact group. A block with nothing at all on file is
  // skipped entirely rather than shown empty.
  const contactBlocks = [
    {
      label: 'Emergency Contact',
      icon: 'contact_emergency',
      name: reg.emergency_contact_name,
      relationship: reg.emergency_contact_relationship,
      email: reg.emergency_contact_email,
      phones: [
        { label: 'Phone', value: reg.emergency_contact_phone },
        { label: 'Cell', value: reg.emergency_contact_cell },
        { label: 'Work', value: reg.emergency_contact_work_phone },
      ],
      addresses: [
        { label: 'Home', value: formatAddress(reg.emergency_contact_home_address, reg.emergency_contact_city, reg.emergency_contact_postal_code) },
        { label: 'Work', value: formatAddress(reg.emergency_contact_work_address) },
      ],
    },
    {
      label: 'Mother',
      icon: 'woman',
      name: reg.mother_name,
      email: reg.mother_email,
      phones: [
        { label: 'Home', value: reg.mother_home_phone },
        { label: 'Cell', value: reg.mother_cell },
        { label: 'Work', value: reg.mother_work_phone },
      ],
      addresses: [
        { label: 'Home', value: formatAddress(reg.mother_home_address, reg.mother_city, reg.mother_postal_code) },
        { label: 'Work', value: formatAddress(reg.mother_work_address, reg.mother_work_city, reg.mother_work_postal_code) },
      ],
    },
    {
      label: 'Father',
      icon: 'man',
      name: reg.father_name,
      email: reg.father_email,
      phones: [
        { label: 'Home', value: reg.father_home_phone },
        { label: 'Cell', value: reg.father_cell },
        { label: 'Work', value: reg.father_work_phone },
      ],
      addresses: [
        { label: 'Home', value: formatAddress(reg.father_home_address, reg.father_city, reg.father_postal_code) },
        { label: 'Work', value: formatAddress(reg.father_work_address, reg.father_work_city, reg.father_work_postal_code) },
      ],
    },
  ]
    .map((block) => ({
      ...block,
      phones: block.phones.filter((p) => p.value),
      addresses: block.addresses.filter((a) => a.value),
    }))
    .filter((block) => block.name || block.phones.length > 0 || block.email || block.addresses.length > 0);

  return (
    <div id="content-page" className="content-page">
      <style>{`
        .header-search-row {
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: start;
          gap: 12px;
          width: 100%;
        }
        @media (max-width: 767.98px) {
          .header-search-row {
            grid-template-columns: 1fr;
          }
          .header-search-row .student-search-wrap {
            width: 100%;
          }
        }
        .student-search-wrap { position: relative; }
        .student-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 18px;
          color: #9ca3af;
          pointer-events: none;
        }
        .student-search-input {
          padding-left: 40px;
          padding-right: 34px;
          height: 42px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          background-color: #f9fafb;
          font-size: 14px;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
        }
        .student-search-input:focus {
          background-color: #fff;
          border-color: #6f42c1;
          box-shadow: 0 0 0 3px rgba(111, 66, 193, 0.12);
        }
        .student-search-clear {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          width: 26px;
          height: 26px;
          background: none;
          border: none;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          cursor: pointer;
          border-radius: 50%;
        }
        .student-search-clear:hover {
          background-color: #f3f4f6;
          color: #374151;
        }
        .student-search-clear i {
          font-size: 16px;
        }
      `}</style>
      <Container>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Header>
                <div className="header-search-row">
                  {/* Grid (1fr auto) instead of flex - guarantees the title
                      takes the remaining space and the search box sits
                      flush against the row's right edge, with no flex
                      grow/shrink/wrap ambiguity between the two. */}
                  <div className="header-title mb-2 mb-md-0" style={{ minWidth: 0 }}>
                    <h4 className="card-title">
                      <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>contact_emergency</i>
                      Emergency Contacts
                    </h4>
                    <p className="text-muted mb-0">Emergency, mother & father contact numbers for every student in your class</p>
                  </div>
                  <div className="student-search-wrap" style={{ minWidth: '260px' }}>
                    <i className="material-symbols-outlined student-search-icon">search</i>
                    <input
                      type="text"
                      className="form-control student-search-input"
                      placeholder="Search by student name..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                      <button
                        type="button"
                        className="student-search-clear"
                        aria-label="Clear search"
                        onClick={() => setSearch('')}
                      >
                        <i className="material-symbols-outlined">close</i>
                      </button>
                    )}
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" role="status" style={{ color: '#6f42c1' }}>
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3 text-muted mb-0">Loading students...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-5">
                    <i className="material-symbols-outlined" style={{ fontSize: '48px', color: '#dc3545' }}>error</i>
                    <p className="text-danger mt-2 mb-0">{error}</p>
                  </div>
                ) : filteredStudents.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="material-symbols-outlined" style={{ fontSize: '64px', color: '#ccc' }}>group_off</i>
                    <p className="mt-3 text-muted mb-0">
                      {students.length === 0 ? 'No students are currently assigned to your class(es).' : 'No students match your search.'}
                    </p>
                  </div>
                ) : (
                  <Row className="g-3">
                    {filteredStudents.map((student) => (
                      <Col xs="12" md="6" lg="4" key={student.id}>
                        <BootstrapCard
                          className="h-100 shadow-sm"
                          role="button"
                          onClick={() => handleViewDetails(student)}
                          style={{ cursor: 'pointer', border: '1px solid #eef0f3', borderRadius: '12px' }}
                        >
                          <BootstrapCard.Body className="d-flex align-items-center gap-3">
                            {student.photo ? (
                              <img
                                src={student.photo}
                                alt={student.name}
                                style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }}
                              />
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center text-white flex-shrink-0"
                                style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#6f42c1', fontSize: '16px', fontWeight: 600 }}
                              >
                                {student.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                            )}
                            <div className="flex-grow-1">
                              <div className="fw-bold text-dark">{student.name}</div>
                              {student.class_name && (
                                <div className="text-muted small">{student.class_name}</div>
                              )}
                            </div>
                            <i className="material-symbols-outlined text-muted">chevron_right</i>
                          </BootstrapCard.Body>
                        </BootstrapCard>
                      </Col>
                    ))}
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{detailStudent?.name}'s Emergency Contacts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" style={{ color: '#6f42c1' }}>
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : detailError ? (
            <div className="text-center py-4">
              <i className="material-symbols-outlined" style={{ fontSize: '48px', color: '#dc3545' }}>error</i>
              <p className="text-danger mt-2 mb-0">{detailError}</p>
            </div>
          ) : contactBlocks.length === 0 ? (
            <div className="text-center py-4">
              <i className="material-symbols-outlined" style={{ fontSize: '48px', color: '#ccc' }}>contact_emergency</i>
              <p className="text-muted mt-2 mb-0">No contact information on file for this student.</p>
            </div>
          ) : (
            contactBlocks.map((block, index) => (
              <div
                key={index}
                className="p-3 mb-2"
                style={{ background: '#f8f9fa', border: '1px solid #eef0f3', borderRadius: '10px' }}
              >
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="fw-bold text-dark">
                    <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle', fontSize: '18px' }}>{block.icon}</i>
                    {block.label}
                  </div>
                  {block.relationship && (
                    <span className="text-muted small">{block.relationship}</span>
                  )}
                </div>
                {block.name && (
                  <div className="text-dark mb-2" style={{ fontSize: '14px' }}>{block.name}</div>
                )}
                {block.phones.length === 0 ? (
                  <div className="text-muted small fst-italic mb-2">No phone number on file</div>
                ) : (
                  <Row className="g-2 mb-2">
                    {block.phones.map((phone, pIndex) => (
                      <Col xs="6" key={pIndex}>
                        <div className="text-muted" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>{phone.label}</div>
                        <div style={{ fontSize: '14px' }}>
                          <a href={`tel:${phone.value}`}>{phone.value}</a>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
                {block.email && (
                  <div className="mb-2">
                    <div className="text-muted" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>Email</div>
                    <div style={{ fontSize: '14px' }}>
                      <a href={`mailto:${block.email}`}>{block.email}</a>
                    </div>
                  </div>
                )}
                {block.addresses.length > 0 && (
                  <Row className="g-2">
                    {block.addresses.map((address, aIndex) => (
                      <Col xs="12" key={aIndex}>
                        <div className="text-muted" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>{address.label} Address</div>
                        <div style={{ fontSize: '14px' }}>{address.value}</div>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TeacherEmergencyContacts;
